import React from 'react';
import EntitlementStore from '../stores/EntitlementStore';
import EntitlementConstants from '../constants/EntitlementConstants';
import EntitlementActions from '../actions/EntitlementActions';
import ExternalGroupSelect from './ExternalGroupSelect';
import * as Inovx from '@shared';

var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var InovxControls = Inovx.Shared.Controls;

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var ListHelper = Inovx.Shared.Common.ListHelper;

declare var InovxLoader: any;

interface LinkedEntitlementFormProps {
}

interface LinkedEntitlementFormState {
    securityGroup?: ISecurityGroup | any;
    linkedExternalGroups?: IExternalGroup[]
    isAdding?: boolean;
    isEditing?: boolean;
    applicationPermissions?: IApplicationPermission[];

    isActionInProgress?: boolean;
    actionInProgressMsg?: string;
    validation?: IValidationDictionaryObject;
    displaySelectGroup?: boolean;
}

function getActionInProgress(): boolean {
    return SystemStore.isActionInProgress(EntitlementConstants.ENTITLEMENT_ADD) ||
        SystemStore.isActionInProgress(EntitlementConstants.ENTITLEMENT_EDIT);
}

var _actionInProgressMsg: string;
var _displaySelectGroupState: boolean;

function getFormState(): LinkedEntitlementFormState {
    return {
        securityGroup: EntitlementStore.getSecurityGroup(),
        isAdding: EntitlementStore.getIsAdding(),
        isEditing: EntitlementStore.getIsEditing(),
        isActionInProgress: getActionInProgress(),
        actionInProgressMsg: _actionInProgressMsg,
        displaySelectGroup: _displaySelectGroupState,
        applicationPermissions: EntitlementStore.getSecurityGroupDetail().ApplicationPermissions,
        linkedExternalGroups: EntitlementStore.getSecurityGroupDetail().LinkedExternalGroups,
        validation: EntitlementStore.getValidation(EntitlementConstants.ENTITLEMENT_ADD_FORM)
    };
}

class LinkedEntitlementForm extends React.Component<LinkedEntitlementFormProps, LinkedEntitlementFormState> {
    constructor(props: LinkedEntitlementFormProps) {
        super(props)

        this.state = getFormState();
        _displaySelectGroupState = false;
    }

    private _onChange = (event: any) => {
        EntitlementActions.editSecurityGroup(EntitlementConstants.ENTITLEMENT_ADD_FORM, event.target.name, event.target.value);
    }

    private _loadState = () => {
        this.setState(getFormState());
    }

    private _systemChange= () => {
        this.setState({
            isActionInProgress: getActionInProgress()
        });
    }

    private _validateEntitlement = () => {
        if (!EntitlementStore.getIsValidEditObject()) {
            EntitlementActions.displayInvalidEdit();

            return false;
        }

        return EntitlementStore.getIsValidObject(EntitlementConstants.ENTITLEMENT_ADD_FORM);
    }

    private _saveEntitlement = () => {
        if (this.state.securityGroup.IsLinkedGroup && this.state.linkedExternalGroups.length == 0) {
            var checkMessage = InovxLoader.t('Entitlement:message.linkedGroupEmpty');
            var messageObject = {
                message: checkMessage
            }
            SystemActions.displayWarningMessage(messageObject);
        }
        else {
            if (this._validateEntitlement()) {
                if (!EntitlementActions.checkFacilityPermission()) {
                    var messageObject = {
                        message: InovxLoader.t("Entitlement:message.useFacilityExplorerPermissionError")
                    };
                    SystemActions.displayWarningMessage(messageObject);
                    return;
                }
                if (this.state.isAdding) {
                    EntitlementActions.createEntitlement();
                } else {
                    EntitlementActions.updateEntitlement();
                }
                this.setState({ actionInProgressMsg: InovxLoader.t('Entitlement:form.saving') });
            }
            else {
                this.setState({});
            }
        }
    }

    private _cancel = () => {
        EntitlementActions.finishEdit();
    }

    private _updatePermissions = (id: string, event: any) => {
        EntitlementActions.editPermissions(id, event.target.checked);
    }

    private _isEmptyOrNull = (value: string) => {
        return value && value.length > 0;
    }

    private _checkForUnsavedChanges = (event: any) => {
        if (EntitlementStore.getHasChanges()) {
            event.returnValue = InovxLoader.t('common:msgBox.browserSaveChanges');
        };
    }

    private _displayGroupSearch = () => {
        this.setState({ displaySelectGroup: true });
    }
    private _removeExternalGroup = (e: Event, group: IExternalGroup) => {
        EntitlementActions.deleteExternalGroup(group);
    }

    private _validateExternalGroup = (group: IExternalGroup) => {
        var assignedGroups = this.state.linkedExternalGroups;
        var messageObject: any = {
            message: InovxLoader.t("Entitlement:message.duplicateGroup")
        };
        var dupGroup: string = null;

        for (var j = 0; j < assignedGroups.length; j++) {
            var assignedGroup = assignedGroups[j];
            if (group.ID === assignedGroup.ID) {
                dupGroup = group.DisplayName;
                break;
            }
        }
        messageObject.message += dupGroup;
        if (dupGroup) SystemActions.displayWarningMessage(messageObject);
    }

    private _addExternalGroup = (group: IExternalGroup) => {
        this._validateExternalGroup(group);
        if (group) {
            EntitlementActions.addExternalGroup(group);
        }
    }

    private _closeSelectGroup = () => {
        _displaySelectGroupState = false;
        this.setState({
            displaySelectGroup: _displaySelectGroupState
        });
    }

    componentDidMount() {
        EntitlementStore.addChangeListener(this._loadState);
        SystemStore.addChangeListener(this._systemChange);
        window.addEventListener('beforeunload', this._checkForUnsavedChanges);
    }

    componentWillUnmount() {
        EntitlementStore.removeChangeListener(this._loadState);
        SystemStore.removeChangeListener(this._systemChange);
        window.removeEventListener('beforeunload', this._checkForUnsavedChanges);
    }

    render() {
        ListHelper.sort(this.state.applicationPermissions, "ApplicationDisplayName");

        var inputObj = {
            className: 'form-control',
            type: 'text'
        }
        var securityGroup = this.state.securityGroup;

        var getSystemSecurityGroupDisplayName = (name: string) => {
            switch (name) {
                case 'System Administrator': return InovxLoader.t('Entitlement:systemGroups.systemAdministrator.name');
                case 'Project Administrator': return InovxLoader.t('Entitlement:systemGroups.projectAdministrator.name');
                case 'User': return InovxLoader.t('Entitlement:systemGroups.user.name');
                default: return name;
            }
        };
        var getSystemSecurityGroupDisplayDescription = (name: string, description: string) => {
            switch (name) {
                case 'System Administrator': return InovxLoader.t('Entitlement:systemGroups.systemAdministrator.description');
                case 'Project Administrator': return InovxLoader.t('Entitlement:systemGroups.projectAdministrator.description');
                case 'User': return InovxLoader.t('Entitlement:systemGroups.user.description');
                default: return description;
            }
        };

        var nameInput = {
            ...inputObj,
            name: 'Name',
            id: 'entApp-groupName-input',
            value: securityGroup.IsSystemDefault ? getSystemSecurityGroupDisplayName(securityGroup.Name) : securityGroup.Name
        };

        var descriptionInput = {
            ...inputObj,
            name: 'Description',
            id: 'entApp-desc-input',
            value: securityGroup.IsSystemDefault ? getSystemSecurityGroupDisplayDescription(securityGroup.Name, securityGroup.Description) : securityGroup.Description
        };

        var header = this.state.isAdding ? InovxLoader.t('Entitlement:header.newLinkedSecurityGroup') : InovxLoader.t('Entitlement:header.editSecurityGroup');

        var gridWidths = {
            permissionName: { width: "calc(40% - 40px)" },
            enabled: { width: "80px" },
            description: { width: "calc(60% - 40px)" }
        };

        var linkedGroupNameLabel = InovxLoader.t('Entitlement:form.linkedGroup');

        var columns = [{
            field: "DisplayName",
            headerText: InovxLoader.t('Entitlement:grid.name'),
            type: "string"
        }];
        var commands = {
            command: [
                {
                    iconClassName: 'glyphicon glyphicon-trash',
                    className: 'grid-button',
                    click: this._removeExternalGroup
                }],
            width: "85px",
            headerText: InovxLoader.t('Entitlement:grid.commands')
        };
        var defaultSort = [{
            field: "DisplayName",
            dir: "ascending"
        }];

        return (
            <InovxControls.StaticHeaderForm title={header} saveOnClick={this._saveEntitlement} saveBtnId="entApp-save-btn" cancelOnClick={this._cancel} cancelBtnId="entApp-cancel-btn"
                isActionInProgress={this.state.isActionInProgress} inProgressMessage={this.state.actionInProgressMsg}>

                <div className="row entitlement-linked-group">
                    <div className="col-sm-12">
                        <div className="form-field">
                            <label className="control-label">{`${linkedGroupNameLabel}*`}</label>
                        </div>
                        <div>
                            <Inovx.Shared.Controls.Grid dataSource={this.state.linkedExternalGroups} commands={commands} columns={columns}
                                defaultSort={defaultSort} />
                            <a id="entitlementApp-addLinkedGroup-btn" onClick={this._displayGroupSearch} style={{ height: "34px", marginTop: "8px" }} className="btn btn-info" >{InovxLoader.t("Entitlement:form.searchDirectory")}</a>
                        </div>
                    </div>
                </div>


                <InovxControls.FormField title={`${InovxLoader.t("Entitlement:form.name")}${securityGroup.IsSystemDefault ? '' : '*'}`} inputName='Name'>
                    <InovxControls.ValidationInput {...nameInput} className="form-control" onChange={this._onChange} readOnly={securityGroup.IsSystemDefault} validation={this.state.validation['Name']}/>
                </InovxControls.FormField>

                <InovxControls.FormField title={InovxLoader.t("Entitlement:form.description") } inputName='Description'>
                    <input {...descriptionInput} onChange={this._onChange} readOnly = {securityGroup.IsSystemDefault} />
                </InovxControls.FormField>

                <InovxControls.TabControl tabs={[InovxLoader.t("Entitlement:form.permissions")]}>
                    <InovxControls.Content >
                        <div className="entitlement-permissions">
                            {this.state.applicationPermissions.map((applicationPermission) => {
                                ListHelper.sort(applicationPermission.Permissions, "DisplayName");
                                return (
                                    <InovxControls.Expander title={applicationPermission.ApplicationDisplayName} key={applicationPermission.ApplicationDisplayName} >
                                        <div className="tg-table">
                                            <div className="tg-grid">
                                                <div className="tg-header-container">
                                                    <div className="tg-header tg-row">
                                                        <div className="tg-cell" style={gridWidths.permissionName}>
                                                            <div className="tg-hdr-ctnt">
                                                                <div className="tg-title">{InovxLoader.t('Entitlement:permission.name') }</div>
                                                            </div>
                                                        </div>
                                                        <div className="tg-cell" style={gridWidths.enabled}>
                                                            <div className="tg-hdr-ctnt">
                                                                <div className="tg-title">{InovxLoader.t('Entitlement:permission.enabled') }</div>
                                                            </div>
                                                        </div>
                                                        <div className="tg-cell" style={gridWidths.description}>
                                                            <div className="tg-hdr-ctnt">
                                                                <div className="tg-title">{InovxLoader.t('Entitlement:permission.description') }</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="tg-content-container" style={{ overflow: 'initial' }}>
                                                <div className='tg-content'>
                                                    {applicationPermission.Permissions.map((permission) => {
                                                        return (
                                                            <div className='tg-row' data-uid={permission.ID} key={permission.ID}>
                                                                <div style={gridWidths.permissionName} className='tg-cell'>{permission.DisplayName}</div>
                                                                <div style={gridWidths.enabled} className='tg-cell'> <input type="checkbox"  onChange={(event) => this._updatePermissions(permission.ID, event)}
                                                                    disabled = {securityGroup.IsSystemDefault}
                                                                    checked={ permission.Assigned}  className="checkbox"/></div>
                                                                <div style={gridWidths.description} className='tg-cell'>{permission.Description}</div>
                                                            </div>
                                                        );
                                                    }) }
                                                </div>
                                            </div>
                                        </div>
                                    </InovxControls.Expander>
                                );
                                }
                            ) }
                        </div>
                        </InovxControls.Content>
                </InovxControls.TabControl>

                <InovxControls.ModalPopUp isOpen={this.state.displaySelectGroup} >
                    <ExternalGroupSelect onSelect={this._addExternalGroup} onCancel={this._closeSelectGroup} />
                </InovxControls.ModalPopUp>
           </InovxControls.StaticHeaderForm>
        );
    }
}

export default LinkedEntitlementForm;
